import { Injectable } from '@angular/core';
import * as pdfMake from 'pdfmake/build/pdfmake.js';
import * as pdfFonts from 'pdfmake/build/vfs_fonts.js';
import { PdfDoc } from './pdf-doc';
import { PdfColDef } from './pdf-col-def';
import { PdfCellType } from './pdf-cell-type.enum';
import { List } from 'immutable';
import { ReservationCompare } from '../../event/models/reservation-compare';
import { OnyxEvent } from '../../event/models/event';
import { Constants } from '../constants';
import { ReservationStatusPipe } from '../pipes/reservation-status.pipe';
import { TranslateService } from '@ngx-translate/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { MatchTypeEnum } from '../../event/models/match-type.enum';
import { Reservation } from '../../event/models/reservation';
import { Rsvp } from '../../event/models/rsvp';
import { EventUtils } from 'app/event/utils/event-utils';

@Injectable()
export class PdfService {

  constructor(private translateService: TranslateService,
    private dateFormatPipe: DatePipe,
    private reservationStatusPipe: ReservationStatusPipe,
    private decimalPipe: DecimalPipe) {
    pdfMake.vfs = pdfFonts.pdfMake.vfs;
  }

  downloadRsvpCompare(
    event: OnyxEvent,
    noMatches: List<ReservationCompare>,
    partialMatches: List<ReservationCompare>,
    exactMatches: List<ReservationCompare>): void {

    let pdfDoc = new PdfDoc();

    pdfDoc.addTableHeader(this.buildDocHeader(event));

    pdfDoc.addLineBreak();
    pdfDoc.addLineBreak();

    const hasHeaders = true;
    const dataColDefsNoMatch = this.buildDataColDefs(MatchTypeEnum.NO_MATCH);
    const noMatchesData = this.convertListToData(noMatches, MatchTypeEnum.NO_MATCH);
    pdfDoc.addCard(this.translateService.instant('EVENT.RSVPS.NO_MATCHING_TITLE'), dataColDefsNoMatch, noMatchesData, hasHeaders);

    pdfDoc.addLineBreak();
    pdfDoc.addLineBreak();

    const dataColDefsPartialMatches = this.buildDataColDefs(MatchTypeEnum.PARTIAL_MATCH);
    const partialMatchesData = this.convertListToData(partialMatches, MatchTypeEnum.PARTIAL_MATCH);
    const partialMatchesLines = this.getInnerHorizontalLines(partialMatches, hasHeaders);
    pdfDoc.addCard(this.translateService.instant('EVENT.RSVPS.PARTIAL_MATCHING_TITLE'),
      dataColDefsPartialMatches, partialMatchesData, hasHeaders, partialMatchesLines);

    pdfDoc.addLineBreak();
    pdfDoc.addLineBreak();

    const dataColDefsExactMatches = this.buildDataColDefs(MatchTypeEnum.EXACT_MATCH);
    const exactMatchesData = this.convertListToData(exactMatches, MatchTypeEnum.EXACT_MATCH);
    pdfDoc.addCard(this.translateService.instant('EVENT.RSVPS.EXACT_MATCHING_TITLE'),
      dataColDefsExactMatches, exactMatchesData, hasHeaders);

    const name = EventUtils.getEventName(event, false);
    pdfMake.createPdf(pdfDoc).download((name ? name : '') + Constants.RSVP_COMPARE_PDF_FILE_NAME_SUFFIX);
  }

  buildDocHeader(event: OnyxEvent): string[][] {
    return [
      [
        this.translateService.instant('EVENT.NAME'),
        this.translateService.instant('EVENT.CURRENCY_HEADER'),
        this.translateService.instant('EVENT.RSVPS.PDF.GROUP_CODE'),
        this.translateService.instant('EVENT.CONTACT_PROPERTY'),
        this.translateService.instant('EVENT.START_DATE'),
        this.translateService.instant('EVENT.END_DATE')
      ],
      [
        EventUtils.getEventName(event, false),
        event.currency,
        event.eventProperty ? event.eventProperty.eventReferenceNumber : '',
        event.eventProperty ? event.eventProperty.name : '',
        this.dateFormatPipe.transform(event.eventProperty.startDate, Constants.DATE_FORMAT_MM_DD_YYYY),
        this.dateFormatPipe.transform(event.eventProperty.endDate, Constants.DATE_FORMAT_MM_DD_YYYY)
      ]
    ];
  }

  buildDataColDefs(matchType: MatchTypeEnum): PdfColDef[]  {
    let dataColDefs: PdfColDef[] = [];
    if (matchType === MatchTypeEnum.PARTIAL_MATCH) {
      dataColDefs.push(new PdfColDef(this.translateService.instant('EVENT.RSVPS.TYPE'), PdfCellType.TEXT, 'auto'));
    }
    dataColDefs.push(new PdfColDef(this.translateService.instant('EVENT.RSVPS.NAME'), PdfCellType.TEXT, '*'));
    if (matchType === MatchTypeEnum.PARTIAL_MATCH || matchType === MatchTypeEnum.EXACT_MATCH) {
      dataColDefs.push(new PdfColDef(this.translateService.instant('EVENT.RESERVATION.CONFIRMATION_NUMBER')));
      dataColDefs.push(new PdfColDef(this.translateService.instant('EVENT.RESERVATION.STATUS')));
      dataColDefs.push(new PdfColDef(this.translateService.instant('EVENT.RESERVATION.ROOM_TYPE')));
      dataColDefs.push(new PdfColDef(this.translateService.instant('EVENT.RESERVATION.CHECKIN_DATE'), PdfCellType.DATE));
      dataColDefs.push(new PdfColDef(this.translateService.instant('EVENT.RESERVATION.CHECKOUT_DATE'), PdfCellType.DATE));
    }
    dataColDefs.push(new PdfColDef(this.translateService.instant('EVENT.RESERVATION.NUMBER_NIGHTS'), PdfCellType.NUMBER));
    dataColDefs.push(new PdfColDef(this.translateService.instant('EVENT.RESERVATION.ROOM_RATE'), PdfCellType.NUMBER));
    dataColDefs.push(new PdfColDef(this.translateService.instant('EVENT.RESERVATION.REVENUE'), PdfCellType.NUMBER));
    dataColDefs.push(new PdfColDef(this.translateService.instant('EVENT.RESERVATION.COMMISSION'), PdfCellType.NUMBER));
    return dataColDefs;
  }

  convertListToData(list: List<ReservationCompare>, matchType: MatchTypeEnum): string[][] {
    let data: string[][] = [];
    list.forEach(reservationCompare => {
      if (reservationCompare.reservation) {
        data.push(this.convertObjectToRow(reservationCompare.reservation, matchType));
      }
      if (matchType !== MatchTypeEnum.EXACT_MATCH && reservationCompare.rsvp) {
        data.push(this.convertObjectToRow(reservationCompare.rsvp, matchType));
      }
    });
    return data;
  }

  getInnerHorizontalLines(list: List<ReservationCompare>, hasHeaders: boolean): number[] {
    let lines: number[] = [];
    let line = hasHeaders ? 1 : 0;
    list.forEach(reservationCompare => {
      if (reservationCompare.reservation && reservationCompare.rsvp) {
        line += 2;
      } else {
        line++;
      }
      lines.push(line);
    });
    return lines;
  }

  convertObjectToRow(object: Reservation | Rsvp, matchType: MatchTypeEnum): string[] {
    let row = [];
      if (matchType === MatchTypeEnum.PARTIAL_MATCH) {
        if ((<Reservation>object).eventReservationId) {
          row.push(this.translateService.instant('EVENT.RSVPS.PDF.TYPE_RESERVATION'));
        } else if ((<Rsvp>object).eventRsvpId) {
          row.push(this.translateService.instant('EVENT.RSVPS.TYPE_RSVP'));
        }
      }
      row.push(object.lastName + ', ' + object.firstName);
      if (matchType === MatchTypeEnum.PARTIAL_MATCH || matchType === MatchTypeEnum.EXACT_MATCH) {
        if ((<Reservation>object).eventReservationId) {
          row.push((<Reservation>object).confirmation);
          row.push(this.reservationStatusPipe.transform((<Reservation>object).status));
          row.push((<Reservation>object).roomType);
          row.push((<Reservation>object).checkInDate);
          row.push((<Reservation>object).checkOutDate);
        } else if ((<Rsvp>object).eventRsvpId) {
          row.push('');
          row.push('');
          row.push('');
          row.push('');
          row.push('');
        }
      }
      row.push(object.nights);
      row.push(this.decimalPipe.transform(object.roomRate, '1.2-2'));
      row.push(this.decimalPipe.transform(object.revenue, '1.2-2'));
      row.push(this.decimalPipe.transform(object.commission, '1.2-2'));
      return row;
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/shared/pdf/pdf.service.ts